<template>
    <button
        v-on="listeners"
        v-bind="attrs"
        :class="[custom, sizeClasses, modeClasses, isLoading, {'cursor-not-allowed': disabled}]"
        :disabled="disabled"
        class="cbutton"
        :aria-label="ariaLabel"
        :type="type"
        :ref="refName"
        >
        <span v-if="text" class="flex items-center">
            <base-icon 
              :name="icon" 
              :size="sizeIcon" 
              class="mr-1" 
              :class="[{'animate-color-pulse-error' : mode === 'primary-phantom-pulse'}, iconClass]"
              v-if="hasIcon && !loading"/>
            <span>{{ text }}</span>
        </span>
        <slot v-else />
    </button>
</template>

<script>
import icons from '@/components/icons/_requireIcons'
export default {
  name: 'base-button',
  inheritAttrs: false,
  props: {
    text: {
      type: String,
      default: ''
    },
    type: {
      type: String,
      default: 'button',
    },
    mode: {
      type: String,
      default: 'primary',
      validator: function (value) {
        return [
          'primary', 
          'staticprimary', 
          'secondary', 
          'staticsecondary', 
          'primary-phantom', 
          'primary-phantom-pulse', 
          'staticprimary-phantom', 
          'secondary-phantom', 
          'secondary-phantom-blue', 
          'primary-line', 
          'tools', 
          'core']
          .indexOf(value) !== -1
      }
    },
    icon: {
      type: String,
      default: 'none',
      validator (value) {
        return Object.prototype.hasOwnProperty.call(icons, value)
      }
    },
    size: {
      type: String,
      default: 'md',
      validator: function (value) {
        return ['xs', 'sm', 'md', 'xs-button', 'md-button', 'lg'].indexOf(value) !== -1
      }
    },
    sizeIcon: {
      type: String,
      default: 'sm',
    },
    iconClass: {
      type: [String, Object, Array],
      default: '',
    },
    custom: {
      type: String,
      default: ''
    },
    loading: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    ariaLabel: {
      type: String,
      default: null,
    },
    refName: {
      type: String,
      default: 'baseBtn',
    },
  },
  computed: {
    listeners: function () {
      return {
        ...this.$listeners
      }
    },
    isLoading: function () {
      return { 'loader-in-element': this.loading }
    },
    attrs: function () {
      return {
        ...this.$attrs
      }
    },
    hasIcon: function () {
      return this.icon !== 'none'
    },
    modeClasses: function () {
      return {
        'cbutton--primary': this.mode === 'primary',
        'cbutton--staticprimary': this.mode === 'staticprimary',
        'cbutton--secondary text-white bg-secondary dark:bg-darkblue-600 hover:bg-secondary-800 dark:hover:bg-darkblue-500 active:bg-secondary-800 dark:active:bg-darkblue focus:bg-secondary-800': this.mode === 'secondary',
        'cbutton--secondary text-white bg-staticsecondary dark:bg-staticdarkblue-600 hover:bg-staticsecondary-800 dark:hover:bg-staticdarkblue-500 active:bg-staticsecondary-800 dark:active:bg-staticdarkblue focus:bg-staticsecondary-800': this.mode === 'staticsecondary',
        'cbutton--prim-phantom bg-primary-50 dark:bg-darkblue text-primary-800 dark:text-primary-500 hover:text-primary-800 dark:hover:text-white hover:bg-primary-100 dark:hover:bg-primary focus:bg-primary-100 focus:text-primary-800': this.mode === 'primary-phantom',
        'cbutton--prim-phantom-pulse bg-primary-50 dark:bg-darkblue text-primary-800 dark:text-primary-500 hover:text-primary-800 dark:hover:text-white hover:bg-primary-100 dark:hover:bg-primary focus:bg-primary-100 focus:text-primary-800': this.mode === 'primary-phantom-pulse',
        'cbutton--prim-phantom bg-staticprimary-50 dark:bg-staticdarkblue text-staticprimary-800 dark:text-staticprimary-500 hover:text-staticprimary dark:hover:text-white hover:bg-staticprimary-100 dark:hover:bg-staticprimary-500 focus:bg-staticprimary-100 focus:text-staticprimary': this.mode === 'staticprimary-phantom',
        'cbutton--sec-phantom bg-secondary-50 dark:bg-darkblue-600 dark:hover:bg-darkblue-500 text-secondary dark:text-darkblue-300 hover:text-secondary-500 dark:hover:text-darkblue-200 focus:bg-secondary-100 focus:text-secondary': this.mode === 'secondary-phantom',
        'cbutton--prim-line bg-white dark:bg-darkblue text-primary-800 hover:bg-primary-50 dark:hover:bg-darkblue-600 dark:hover:text-white border-primary-800 active:bg-primary active:text-white dark:active:text-white focus:bg-primary-50 focus:text-primary-900': this.mode === 'primary-line',
        'cbutton--tools text-white hover:bg-secondary-900 dark:hover:bg-darkblue active:bg-secondary dark:active:bg-darkblue active:text-white focus:bg-secondary-900 dark:focus:bg-darkblue': this.mode === 'tools',
        'cbutton--core bg-white dark:bg-darkblue text-secondary dark:text-darkblue-200 hover:bg-primary-50 dark:hover:bg-darkblue-600 hover:text-primary-900 dark:hover:text-primary active:bg-primary-100 active:text-primary focus:bg-primary-100 focus:text-primary-900 hover:font-bold': this.mode === 'core',
        'cbutton--sec-phantom-blue bg-secondary-50 dark:bg-darkblue-600 text-secondary dark:text-darkblue-100 hover:bg-primary-800 hover:text-white dark:hover:bg-primary-600 dark:hover:text-white focus:text-white focus:bg-primary-800 dark:focus:bg-primary-600': this.mode === 'secondary-phantom-blue',
      }
    },
    sizeClasses: function () {
      return {
        'h-8 px-4': this.size === 'xs',
        'h-8 pl-2 pr-1': this.size === 'xs-button',
        'h-9 px-4': this.size === 'sm',
        'h-10 px-4 md:px-6': this.size === 'md',
        'h-10 pl-2 pr-1': this.size === 'md-button',
      }
    },
  },
  methods: {
    focus () {
      const refName = this.refName
      this.$refs[refName].focus()
    },
  },
}
</script>
<style lang="scss" scoped>
    .cbutton {
      @apply flex items-center justify-center space-x-1 text-sm font-semibold rounded focus:outline-none transition-colors duration-300 focus:border-2 focus:border-black dark:focus:border-white;
      &.cbutton--primary {
        @apply text-white bg-primary-800 hover:bg-primary-900 active:bg-primary-900 focus:bg-primary-900;
      }
      &.cbutton--staticprimary {
        @apply text-white bg-staticprimary-800 hover:bg-staticprimary-900 active:bg-staticprimary-900 focus:bg-staticprimary-900;
      }
      &.cbutton--prim-line {
        @apply  border-2 border-solid focus:outline-none;
      }
      &.cbutton--core {
        @apply  focus:outline-none;
      }
      &.cbutton--tools {
        @apply focus:border-white;
      }
      &:active {
        transform: scale(0.96);
      }
      &:focus {
        outline: 2px solid black !important; 
        outline-offset: 2px !important;
      }
    }
</style>
